/**
 * commitmentSheetModality — pure modality-default decision for #2089.
 *
 * `CommitmentSheet.jsx` initialised its modality toggle to a hardcoded
 * 'voice' with no regard for `scenario.voice_available` (derived server-side
 * from `allowed_modality !== 'text_only'`, see `backend/.../discoveryScoring.js`
 * `voiceAvailable()`) — the catalogue's ~2 text-only scenarios could be
 * committed toward a voice launch from this sheet.
 *
 * ChatPage's own modality guard (`frontend/src/pages/ChatPage.jsx`, the
 * `allowedModality === 'text_only' && chatMode === 'voice'` effect) already
 * catches this downstream — once `run.allowed_modality` resolves after the
 * run loads, a preselected voice mode gets cleared and the mode-select
 * picker re-shows. So this scenario was never actually launchable into a
 * real voice session; the gap was purely cosmetic (the sheet offered and
 * defaulted to a mode the very next screen would silently reject). This
 * function is the sheet-level fix: never offer or default to voice when
 * the card itself says it isn't available.
 *
 * Extracted as a pure function (no React, no jsdom) so the decision table
 * is unit-testable directly — mirrors the `contextPresentation.js` /
 * `chooserQuestions.js` precedent in this same directory.
 *
 * @param {{voice_available?: boolean}|null|undefined} scenario - a
 *   discovery card (see `toDiscoveryCard` in `backend/src/routes/demo.js`).
 *   `voice_available` is only ever `false` for a genuine text_only
 *   scenario — absent/undefined (e.g. a test fixture, or a future card
 *   shape that drops the field) is treated as available, matching the
 *   card builder's own `!== 'text_only'` default-permissive posture.
 * @param {'voice'|'text'|null|undefined} preferredModality - an
 *   upstream preference (e.g. the #1922 chooser's Q3 answer). Only
 *   honoured when it's actually usable on this scenario.
 * @returns {'voice'|'text'}
 */
export function computeInitialModality(scenario, preferredModality) {
  const voiceAvailable = scenario?.voice_available !== false;

  if (preferredModality === 'text') return 'text';
  if (preferredModality === 'voice') return voiceAvailable ? 'voice' : 'text';

  // Gate decision (mobile-guest-journey-2026-07): voice is the default
  // nudge, when the scenario actually supports it.
  return voiceAvailable ? 'voice' : 'text';
}
